import React, { useEffect, useState } from 'react';
import { Save, Loader2, CheckCircle2 } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from './api';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';

const SECTIONS = [
  { key: 'about',          ar: 'التعريف بالشركة',        en: 'Company introduction' },
  { key: 'vision',         ar: 'الرؤية',                  en: 'Vision' },
  { key: 'mission',        ar: 'الرسالة',                 en: 'Mission' },
  { key: 'values',         ar: 'القيم',                   en: 'Values' },
  { key: 'responsibility', ar: 'المسؤولية الوطنية',       en: 'National responsibility' },
  { key: 'legal',          ar: 'الكيان القانوني',         en: 'Legal entity' },
];

/** Editor for the singleton company_info blob. Each section has ar/en text. */
const AdminCompany = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: null, msg: '' });

  useEffect(() => {
    adminApi.get('/settings/company_info')
      .then(({ data }) => setData(data.value || {}))
      .finally(() => setLoading(false));
  }, []);

  const upd = (section, lang, value) => {
    setData((prev) => ({ ...prev, [section]: { ...(prev[section] || {}), [lang]: value } }));
  };

  const save = async () => {
    setSaving(true); setStatus({ type: null, msg: '' });
    try {
      await adminApi.put('/settings/company_info', { value: data });
      setStatus({ type: 'success', msg: isAr ? 'تم الحفظ بنجاح' : 'Saved successfully' });
      setTimeout(() => setStatus({ type: null, msg: '' }), 2500);
    } catch (e) {
      setStatus({ type: 'error', msg: formatApiErrorDetail(e?.response?.data?.detail) });
    } finally { setSaving(false); }
  };

  const txt = isAr ? 'text-right' : 'text-left';
  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <h1 className={`text-2xl md:text-3xl font-bold text-slate-900 dark:text-white ${txt}`}>{t.company}</h1>

      {SECTIONS.map((s) => (
        <div key={s.key} className={`rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 ${txt}`}>
          <h2 className="text-base font-bold text-slate-900 dark:text-white mb-3">{isAr ? s.ar : s.en}</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">{isAr ? 'عربي' : 'Arabic'}</label>
              <textarea rows={5} dir="rtl" value={data[s.key]?.ar || ''} onChange={(e) => upd(s.key, 'ar', e.target.value)} className={inputCls + ' text-right leading-loose'} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5">{isAr ? 'إنجليزي' : 'English'}</label>
              <textarea rows={5} dir="ltr" value={data[s.key]?.en || ''} onChange={(e) => upd(s.key, 'en', e.target.value)} className={inputCls + ' text-left leading-loose'} />
            </div>
          </div>
        </div>
      ))}

      <div className={`flex items-center gap-3 sticky bottom-6 ${isAr ? 'flex-row-reverse' : ''}`}>
        <button type="button" onClick={save} disabled={saving} className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow-lg disabled:opacity-60 ${isAr ? 'flex-row-reverse' : ''}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t.save}
        </button>
        {status.type === 'success' && (
          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="w-4 h-4" /> {status.msg}
          </span>
        )}
        {status.type === 'error' && <span className="text-sm text-red-600">{status.msg}</span>}
      </div>
    </div>
  );
};

const inputCls = 'w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

export default AdminCompany;
